// pages/social-login/ssoClient.ts — typed POSTs to the owner-hub SSO backend.
//
// Backend contract: `automation-micros-wl_owner_hub` sso.md + api/user.md#/user/auth. Routes are mounted
// at `{window.config.userApiUrl}/auth/sso/*`. These login routes are unauthenticated (no token header).
// Success returns the standard LoginResponse (identical to `/login`); the two non-token outcomes are
// discriminated by their fields. A 4xx (invalid token, code exchange failed, ticket expired, ...) throws
// from axios and is handled by the caller (the auth-slice thunk maps it to `rejectWithValue`).

import axios from 'axios';

import type { SsoProvider } from './config';

/** The standard authenticated login payload (mirrors `token.ts` `LoginResponse`). */
export interface SsoLoginResponse {
  success: true;
  token: string;
  refresh: string;
  expires: string;
  user: { user_id: string; email: string; email_verified: boolean; admin?: unknown };
  session_id?: string;
  redirect_url?: string;
}

/** Stale-username / existing-account case: the backend emailed a confirmation link instead of logging in. */
export interface SsoLinkSentResponse {
  success: false;
  email_required?: false;
  message: string;
}

/** Unknown Telegram identity: the FE must collect an email and call `postTelegramComplete` with the ticket. */
export interface SsoEmailRequiredResponse {
  success: false;
  email_required: true;
  sso_ticket: string;
}

export type SsoGoogleResult = SsoLoginResponse | SsoLinkSentResponse;
export type SsoTelegramResult = SsoLoginResponse | SsoEmailRequiredResponse | SsoLinkSentResponse;
export type SsoTelegramCompleteResult = SsoLoginResponse | SsoLinkSentResponse;

/** Options every login route accepts (onboarding session to bind, post-auth redirect, new-account language). */
export interface SsoLoginOptions {
  session_id?: string | null;
  redirect_url?: string;
  language?: string;
}

/** Google: either a GIS `id_token`, or a popup-code-flow `{ code, redirect_uri, code_verifier }`. */
export type SsoGoogleBody = SsoLoginOptions &
  ({ id_token: string } | { code: string; redirect_uri: string; code_verifier?: string });

/** Telegram: always the PKCE code flow. */
export type SsoTelegramBody = SsoLoginOptions & { code: string; code_verifier: string; redirect_uri: string };

/** Telegram email-capture completion. */
export type SsoTelegramCompleteBody = SsoLoginOptions & { sso_ticket: string; email: string };

/** Discriminators, so callers branch without touching field-presence directly. */
export function isSsoLogin(result: { success: boolean }): result is SsoLoginResponse {
  return result.success === true;
}
export function isSsoEmailRequired(result: SsoTelegramResult): result is SsoEmailRequiredResponse {
  return result.success === false && (result as SsoEmailRequiredResponse).email_required === true;
}

function authBase(): string {
  const userApiUrl = (window as unknown as { config?: { userApiUrl?: string } }).config?.userApiUrl;
  if (!userApiUrl) {
    throw new Error('window.config.userApiUrl is not configured');
  }
  return `${userApiUrl.replace(/\/+$/, '')}/auth`;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const response = await axios.post<T>(`${authBase()}${path}`, body);
  return response.data;
}

/** POST `/auth/sso/google`. */
export function postGoogle(body: SsoGoogleBody): Promise<SsoGoogleResult> {
  return post<SsoGoogleResult>('/sso/google', body);
}

/** POST `/auth/sso/telegram`. */
export function postTelegram(body: SsoTelegramBody): Promise<SsoTelegramResult> {
  return post<SsoTelegramResult>('/sso/telegram', body);
}

/** POST `/auth/sso/telegram/complete`. */
export function postTelegramComplete(body: SsoTelegramCompleteBody): Promise<SsoTelegramCompleteResult> {
  return post<SsoTelegramCompleteResult>('/sso/telegram/complete', body);
}

// ── Authenticated management (account settings) ─────────────────────────────
// These require the owner's session; the raw access token goes in Authorization (the app's user-API
// convention — no `Bearer` prefix). Admin-impersonation tokens get `FORBIDDEN` from the backend.

/** One linked identity as returned by `GET /auth/sso`. */
export interface SsoIdentityEntry {
  identity_provider: SsoProvider;
  sub: string;
  email?: string;
  phone_number?: string;
  linked_at: number;
  pending?: boolean;
}

const authConfig = (token: string): { headers: { Authorization: string } } => ({
  headers: { Authorization: token },
});

/** GET `/auth/sso` — the identities linked to the caller's account. */
export async function getSsoIdentities(token: string): Promise<SsoIdentityEntry[]> {
  const response = await axios.get<{ sso: SsoIdentityEntry[] }>(`${authBase()}/sso`, authConfig(token));
  return response.data.sso ?? [];
}

/** POST `/auth/sso/link/google` — link a Google identity to the current account (same proof as login). */
export async function linkGoogle(
  token: string,
  body: { id_token: string } | { code: string; redirect_uri: string; code_verifier?: string },
): Promise<void> {
  await axios.post(`${authBase()}/sso/link/google`, body, authConfig(token));
}

/** POST `/auth/sso/link/telegram`. */
export async function linkTelegram(
  token: string,
  body: { code: string; code_verifier: string; redirect_uri: string },
): Promise<void> {
  await axios.post(`${authBase()}/sso/link/telegram`, body, authConfig(token));
}

/** DELETE `/auth/sso/:provider` — unlink an identity (always allowed; email login remains). */
export async function unlinkProvider(token: string, provider: SsoProvider): Promise<void> {
  await axios.delete(`${authBase()}/sso/${provider}`, authConfig(token));
}
